import { MouseEvent, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import DOMPurify from 'isomorphic-dompurify';
import { useDisconnect } from 'wagmi';

import ImageProxy from '@next-image-proxy';

import {
  useCanClaimOfferQuery,
  useClaimOfferUrlQuery,
  useCreateBinancePayMutation,
  useFeatureTogglesQuery,
} from '@query';

import { Card, HorizontalSeparator, useModalController } from '@uikit/index';

import { MIXPANEL_EVENT_NAMES } from '@shared/api/mixPanel';
import { BinanceCheckoutEntry, DeserializedHotOffer } from '@shared/api/types/hotOffers';
import KarmaTag from '@shared/common/components/KarmaTag';
import { AuthWeb3Modal } from '@shared/common/components/modals/AuthWeb3Modal';
import { CompleteYourMagicIdModal } from '@shared/common/components/modals/CompleteYourMagicIdModal';
import { useAuth } from '@shared/common/providers/AuthProvider';
import { useMixPanel } from '@shared/common/providers/MixPanelProvider';
import { useUserAgentDetect } from '@shared/common/providers/UserAgentDetectProvider';
import { HOT_OFFER_TYPE } from '@shared/constants';
import { membershipTiers } from '@shared/constants/membershipTiers';
import displayErrorToast from '@shared/helpers/displayErrorToast';
import { openLinkByTag } from '@shared/helpers/openLinkByTag';
import clearSavedConnectionDetails from '@shared/helpers/web3/clearSavedConnectionDetails';

import HotOfferCheckoutModal from '@store:web/components/pages/HotOfferPage/HotOfferCheckoutModal';
import HotOfferClaimButton from '@store:web/components/pages/HotOfferPage/HotOfferClaimButton';
import HotOfferFailModal from '@store:web/components/pages/HotOfferPage/HotOfferFailModal';
import HotOfferSuccessModal from '@store:web/components/pages/HotOfferPage/HotOfferSuccessModal';
import KarmaLimitModal from '@store:web/components/pages/HotOfferPage/KarmaLimitModal';
import HotOfferShareButton from '@store:web/components/pages/HotOffersPage/HotOfferShareButton';
import HotOfferStats, {
  USDT_SQR_EXCH_CURRENCY,
} from '@store:web/components/pages/HotOffersPage/HotOfferStats';

import AccessModal from '../AccessModal';
import { UnsupportedBrowserModal } from './UnsupportedBrowserModal';

export interface HotOfferCardProps {
  offer: DeserializedHotOffer;
  status?: string;
}

const supportedBrowsers = ['Chrome', 'Firefox', 'Safari', 'Edge', 'Brave', 'Opera', 'Yandex'];

export default function HotOfferCard({ offer: offerFromProps, status }: HotOfferCardProps) {
  const [isOpenMagicIDModal, setIsOpenMagicIDModal] = useState(false);
  const [isOpenEVMModal, setIsOpenEVMModal] = useState(false);
  const [redirectLink, setRedirectLink] = useState<string>('');

  const {
    id = '',
    type = HOT_OFFER_TYPE.OFFER,
    attributes: {
      icon = '',
      name = 'Unnamed',
      offerAmount = '0',
      offerCurrency = 'USDT',
      cashBackPercentage = 0,
      offer = '',
      claimUrl,
      lockPeriod = 1,
      claimKarmaThreshold = 0,
      accessTier = 'FREE',
    } = {},
  } = offerFromProps;
  const karmaLimitModal = useModalController();
  const checkoutModal = useModalController();
  const failModal = useModalController(status === 'fail');
  const successModal = useModalController(status === 'success');
  const accessModal = useModalController();
  const { browserName } = useUserAgentDetect();

  const [binanceCheckoutEntry, setBinanceCheckoutEntry] = useState<
    BinanceCheckoutEntry | undefined
  >();
  const [accessErrorTime, setAccessErrorTime] = useState<number>(0);

  const { disconnectAsync } = useDisconnect();
  const { user, isAuth } = useAuth();
  const { track } = useMixPanel();

  const [isUnsupportedBrowserModalOpen, setUnsupportedBrowserModalOpen] = useState(false);

  const isSupportedBrowser = supportedBrowsers.includes(browserName);

  const handleCloseUnsupportedBrowserModal = () => setUnsupportedBrowserModalOpen(false);

  const trackHotOfferClaimClick = () => {
    if (user?.id) {
      track(MIXPANEL_EVENT_NAMES.HOT_OFFER_CLAIM_BUTTON_CLICK, {
        distinct_id: user.id,
        offer: name,
      });
    }
  };

  const { data: { binanceHotOffers, hotOffersSharing, membership } = {} } =
    useFeatureTogglesQuery();
  const {
    data: canClaimOffer,
    isFetching: isCanClaimOfferLoading,
    error: canClaimOfferError,
    refetch: refetchCanClaimOffer,
  } = useCanClaimOfferQuery(id, {
    enabled: isAuth && Boolean(id),
  });
  const { isFetching: isClaimOfferUrlLoading, refetch: claimOfferUrl } = useClaimOfferUrlQuery(id, {
    enabled: false,
  });

  const { mutateAsync: createBinancePay, isPending: isCreatingBinancePay } =
    useCreateBinancePayMutation();

  const isDisableClaim = useMemo(() => {
    if (isAuth && canClaimOffer?.attributes?.claimed) {
      return true;
    }

    if (
      isAuth &&
      !canClaimOffer?.attributes?.canBeClaimed &&
      canClaimOffer?.attributes.noUserData
    ) {
      return true;
    }

    return false;
  }, [canClaimOffer, isAuth]);

  const updateModalState = async (isOpen: boolean) => {
    clearSavedConnectionDetails();

    await disconnectAsync();

    setIsOpenMagicIDModal(isOpen);

    if (!isOpen) {
      refetchCanClaimOffer();
    }
  };

  const getIsUserAbleToClaimOffer = () => {
    if (!isAuth) {
      setIsOpenEVMModal(true);

      return false;
    }

    if (canClaimOfferError) {
      displayErrorToast({ error: canClaimOfferError });

      refetchCanClaimOffer();

      return false;
    }

    if (!canClaimOffer?.attributes?.canBeClaimed) {
      const {
        karmaTooLow,
        poolExhausted,
        limitReached,
        suspended,
        claimed,
        noUserData,
        membershipTooLow,
        waitForAccess,
      } = canClaimOffer?.attributes || {};

      if (noUserData) {
        updateModalState(true);

        return false;
      } else if (claimed) {
        if (type === HOT_OFFER_TYPE.SO_QUEST_OFFER) {
          return true;
        }

        toast.success('You already claimed this offer', { toastId: 'hot-offer-claim-success' });
      } else if (karmaTooLow) {
        karmaLimitModal.open();
      } else if (poolExhausted) {
        toast.error('Sorry, Hot Offer limit is reached. Please claim another one.');
      } else if (limitReached) {
        toast.error(
          'Offer Unavailable! Sorry, this offer has been fully claimed. Please check out our other exciting offers or try again later.',
        );
      } else if (suspended) {
        toast.error('This offer is not available');
      } else if (membershipTooLow || !!waitForAccess) {
        if (!membership) {
          return false;
        }

        // if 0 then will open low access modal otherwise early access modal
        setAccessErrorTime(membershipTooLow ? 0 : waitForAccess || 0);
        accessModal.open();
      }

      return false;
    }

    return true;
  };

  const handleClaimOfferButtonClick = async (
    event?: MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => {
    const isUserAbleToClaimOffer = getIsUserAbleToClaimOffer();

    if (!isUserAbleToClaimOffer) {
      event?.preventDefault();

      return;
    }

    if (!canClaimOffer?.attributes.claimUrl) {
      try {
        const { status, data, error } = (await claimOfferUrl()) ?? {};

        if (status === 'error') {
          displayErrorToast({ error });
          return;
        }

        trackHotOfferClaimClick();

        const link =
          type === HOT_OFFER_TYPE.SO_QUEST_OFFER ? claimUrl ?? '' : data?.attributes.url ?? '';

        setRedirectLink(link);

        if (isSupportedBrowser) {
          openLinkByTag({ link });
        } else {
          setUnsupportedBrowserModalOpen(true);
        }
      } catch (error) {
        displayErrorToast({ error });
      }
    } else {
      try {
        const { status, error } = await claimOfferUrl();

        if (status === 'error') {
          displayErrorToast({ error });
          return;
        }

        trackHotOfferClaimClick();
      } catch (error) {
        displayErrorToast({ error });
      }
    }
  };

  useEffect(() => {
    if (
      !isDisableClaim &&
      canClaimOffer?.attributes.payoutConfig.directReward &&
      !canClaimOffer?.attributes?.claimed &&
      Boolean(user?.isNewUser)
    ) {
      handleClaimOfferButtonClick();
    }
  }, [isAuth, canClaimOffer?.attributes.payoutConfig.directReward]);

  const handleClaimBinanceHotOfferButtonClick = async () => {
    const isUserAbleToClaimOffer = await getIsUserAbleToClaimOffer();

    if (!isUserAbleToClaimOffer) {
      return;
    }

    try {
      const binancePayEntry = await createBinancePay(id);
      setBinanceCheckoutEntry(binancePayEntry);

      successModal.close();
      failModal.close();
      checkoutModal.open();
      trackHotOfferClaimClick();
    } catch (error) {
      displayErrorToast({
        error,
        replaceErrorMessageCallback: (errorMessage) => {
          if (errorMessage.includes('HotOfferPoolExhaustedError')) {
            return { newMessage: 'Sorry, Hot Offer limit is reached. Please claim another one.' };
          }

          return { newMessage: errorMessage };
        },
      });
    }
  };

  const onSubmit = () => {
    updateModalState(false);
  };

  const pausedText = offerFromProps.attributes.paused;

  return (
    <>
      <Card className="w-full md:max-w-[450px] xl:max-w-[550px] h-fit flex flex-col gap-4 rounded-[24px] overflow-hidden p-4 bg-neutral-50">
        <div className="w-full flex flex-basis-0 gap-5 items-center">
          <ImageProxy
            className="w-[50px] aspect-square rounded-full border border-primary-200 flex-shrink-0"
            src={icon}
            alt={name}
            width={200}
            height={200}
            data-qa="hot-offer-icon"
          />

          <h1
            className="t-h4 leading-6 line-clamp-2 break-words lg:text-xl"
            data-qa="hot-offer-name"
          >
            {name}
          </h1>
        </div>

        <HorizontalSeparator lineClassName="!bg-neutral-100" />

        <div className="flex flex-col gap-1">
          <span className="t-body2 text-neutral-500 leading-6">Offer Details</span>
          <h2
            className="text-neutral-600 leading-[1.7rem]"
            data-qa="hot-offer-details"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(offer) }}
          />
        </div>

        <HorizontalSeparator lineClassName="!bg-neutral-100" />

        <div className="flex items-center justify-between gap-1">
          <span className="text-md text-neutral-500 leading-6">Offer:</span>
          <h2 className="text-neutral-600 leading-[1.7rem]" data-qa="hot-offer-amount">
            {type === HOT_OFFER_TYPE.BINANCE
              ? `${cashBackPercentage}%/Locked for ${lockPeriod} Days`
              : offerCurrency === USDT_SQR_EXCH_CURRENCY
              ? `$${offerAmount} in SQR`
              : `${offerAmount} ${offerCurrency}`}
          </h2>
        </div>

        <HorizontalSeparator lineClassName="!bg-neutral-100" />

        <div className="flex flex-col flex-wrap justify-between gap-4 overflow-hidden">
          <div className="flex items-center">
            <span className="text-neutral-500 flex-1 leading-6">Min. Karma: </span>
            <div className="flex items-center justify-center gap-1 text-secondary-700">
              <KarmaTag points={claimKarmaThreshold} />
              Karma
            </div>
          </div>
        </div>

        {Boolean(membership) && (
          <>
            <HorizontalSeparator lineClassName="!bg-neutral-100" />
            <div className="flex flex-wrap justify-between gap-1 overflow-hidden">
              <span className="flex flex-1 items-center text-neutral-500">Min. Membership:</span>
              <span className="flex text-sm items-center justify-center gap-1 text-secondary-700">
                <div className="text-xs py-1 px-2 border border-secondary bg-secondary-200 rounded-full">
                  {membershipTiers?.[accessTier]?.name}
                </div>
              </span>
            </div>
          </>
        )}

        <HorizontalSeparator lineClassName="!bg-neutral-100" />

        <HotOfferStats offer={offerFromProps} className="py-3" />

        <div className="flex items-center justify-end gap-2">
          {([HOT_OFFER_TYPE.OFFER, HOT_OFFER_TYPE.SO_QUEST_OFFER] as string[]).includes(type) && (
            <HotOfferClaimButton
              isLoading={isClaimOfferUrlLoading}
              isCanClaimOfferLoading={isCanClaimOfferLoading}
              canClaimOffer={canClaimOffer}
              onClick={handleClaimOfferButtonClick}
              data-qa="hot-offer-claim-button"
              pausedText={pausedText}
            />
          )}

          {type === HOT_OFFER_TYPE.BINANCE && (
            <HotOfferClaimButton
              isLoading={isCreatingBinancePay}
              isCanClaimOfferLoading={isCanClaimOfferLoading}
              canClaimOffer={canClaimOffer}
              onClick={handleClaimBinanceHotOfferButtonClick}
              disabledVisually={isDisableClaim}
              pausedText={pausedText}
            />
          )}

          {hotOffersSharing && !pausedText && <HotOfferShareButton offer={offerFromProps} />}

          <KarmaLimitModal karmaPoints={claimKarmaThreshold} modalController={karmaLimitModal} />

          {type === HOT_OFFER_TYPE.BINANCE && binanceHotOffers && (
            <>
              <HotOfferCheckoutModal
                modalController={checkoutModal}
                offer={offerFromProps}
                binanceCheckoutEntry={binanceCheckoutEntry}
              />

              <HotOfferSuccessModal modalController={successModal} />

              <HotOfferFailModal
                modalController={failModal}
                claimUrl={canClaimOffer?.attributes.claimUrl}
                onRetry={handleClaimOfferButtonClick}
              />
            </>
          )}
        </div>
        <CompleteYourMagicIdModal
          isOpenMagicIDModal={isOpenMagicIDModal}
          hideInputFields={Boolean(canClaimOffer?.attributes.payoutConfig.directReward)}
          formIsDisabled={isClaimOfferUrlLoading}
          onSubmit={onSubmit}
          updateModalState={updateModalState}
        />

        <AuthWeb3Modal isOpen={isOpenEVMModal} setIsOpen={setIsOpenEVMModal} />

        <AccessModal
          {...accessModal.props}
          accessTier={accessTier}
          accessErrorTime={accessErrorTime}
          target="_blank"
        />

        <UnsupportedBrowserModal
          link={redirectLink}
          isOpen={isUnsupportedBrowserModalOpen}
          onClose={handleCloseUnsupportedBrowserModal}
        />
      </Card>
    </>
  );
}
